<template>
	<div class="comparison-table-container">
		<ErrorEmptyState
			v-if="userStore.tierCatalogLoadFailed"
			title="Could not load Tier details"
			message="Often this is a temporary network or connectivity issue. Reload to fetch the tier limits again."
			:retrying="retryingCatalogFetch"
			bordered
			@retry="loadTierCatalog"
		/>
		<div
			v-else
			class="comparison-table-wrapper"
		>
			<table class="comparison-table">
				<thead>
					<tr>
						<th class="feature-column-header" />
						<th
							v-for="tier in tierDetails"
							:key="tier.code"
							class="tier-column-header sgnl-paragraph-s-medium"
						>
							<div>{{ tier.title }}</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr class="price-row">
						<td class="feature-name-cell sgnl-paragraph-s-regular sgnl-text-color-default">Price</td>
						<td
							v-for="tier in tierDetails"
							:key="tier.code"
							class="feature-value-cell price-value-cell sgnl-paragraph-l-medium sgnl-text-color-default"
						>
							<span
								v-if="tier.code === userSubscriptionTierCode"
								class="tier-current-plan-label sgnl-paragraph-s-medium sgnl-text-color-brand"
								>Your current plan</span
							>
							<template v-else>
								{{ getPriceDisplay(tier) }}
							</template>
						</td>
					</tr>

					<template
						v-for="section in comparisonSections"
						:key="section.title"
					>
						<tr class="section-row">
							<td
								class="section-title-cell sgnl-paragraph-s-semibold sgnl-text-color-default"
								:colspan="tierDetails.length + 1"
							>
								{{ section.title }}
							</td>
						</tr>
						<tr
							v-for="feature in section.features"
							:key="feature.name"
						>
							<td class="feature-name-cell sgnl-paragraph-s-regular sgnl-text-color-default">
								{{ feature.name }}
							</td>
							<td
								v-for="(val, tierIndex) in feature.values"
								:key="tierIndex"
								class="feature-value-cell sgnl-paragraph-s-regular sgnl-text-color-default"
							>
								<template v-if="val.type === 'boolean'">
									<CheckmarkFilled16
										v-if="val.value"
										class="sgnl-icon-color-success"
									/>
									<CloseOutline16
										v-else
										class="sgnl-icon-color-tertiary"
									/>
								</template>
								<SkeletonBlock v-else-if="val.type === 'loading'" />
								<span v-else>{{ val.value }}</span>
							</td>
						</tr>
					</template>

					<!-- Action button row -->
					<tr class="action-row">
						<td class="feature-name-cell sgnl-paragraph-s-regular sgnl-text-color-default" />
						<td
							v-for="tier in tierDetails"
							:key="tier.code"
							class="feature-value-cell action-value-cell sgnl-text-color-default"
						>
							<v-btn
								:variant="getButtonVariant(tier)"
								:color="getButtonColor(tier)"
								:disabled="getButtonDisabled(tier)"
								@click="handleButtonClick(tier)"
								block
								>{{ getButtonText(tier) }}</v-btn
							>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div
			v-if="showsSafetyLimitFootnote"
			class="sgnl-caption-regular sgnl-text-color-secondary"
		>
			* A safety limit applies, set high enough that normal use will not reach it. If you do reach it, contact
			<a
				class="sgnl-link"
				href="mailto:developer-support@signaloid.com"
				>developer-support@signaloid.com</a
			>.
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
	listedTiers,
	AccountTierDetail,
	AccountGroupCode,
	CognitoAccountGroup,
	catalogEntryForTier,
	compareTierCodes,
	groupCode,
	isUnlimitedLimit,
	thousandsSeparated,
} from "@/js/tierconfig";
import { SubscriptionConfig } from "@/types/api/subscriptions";
import { TierLimits } from "@/types/api/tiers";
import { useUserStore } from "@/stores/user";
import { toStringWithByteMagnitude, toStringWithDurationMagnitude, toStringWithMagnitude } from "@/js/utilities";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";

// Carbon Icons
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import CloseOutline16 from "@carbon/icons-vue/es/close--outline/16";

type FeatureValue = {
	type: "text" | "boolean" | "loading";
	value?: string | boolean;
};

type ComparisonRow = {
	name: string;
	values: FeatureValue[];
};

type ComparisonSection = {
	title: string;
	features: ComparisonRow[];
};

type LimitValue = TierLimits[keyof TierLimits] | undefined;

type LimitFormatter = (value: LimitValue) => FeatureValue;

type FeatureSpec =
	| { name: string; limit: keyof TierLimits; format?: LimitFormatter }
	| { name: string; includedIn: (tierCode: AccountGroupCode) => boolean };

type FeatureSection = {
	title: string;
	features: FeatureSpec[];
};

const LOADING_CELL: FeatureValue = { type: "loading" };

const ABSENT_CELL: FeatureValue = { type: "text", value: "--" };

const SAFETY_LIMITED_TEXT = "Unlimited*";

function textCell(value: string): FeatureValue {
	return { type: "text", value };
}

const ENTERPRISE_TIER_CODES: AccountGroupCode[] = ["enterprise-tier-s", "enterprise-tier-m", "enterprise-tier-l"];

function onEnterpriseTiers(tierCode: AccountGroupCode): boolean {
	return ENTERPRISE_TIER_CODES.includes(tierCode);
}

function onEveryTier(): boolean {
	return true;
}

function numericCell(format: (limit: number) => string): LimitFormatter {
	return (value) => {
		if (value === undefined) {
			return ABSENT_CELL;
		}
		if (isUnlimitedLimit(value)) {
			return textCell(SAFETY_LIMITED_TEXT);
		}
		return textCell(typeof value === "number" ? format(value) : "Unlimited");
	};
}

const quantityCell = numericCell(thousandsSeparated);

function listLengthCell(value: LimitValue): FeatureValue {
	return textCell(Array.isArray(value) ? String(value.length) : "");
}

function includedCell(value: LimitValue): FeatureValue {
	return { type: "boolean", value: value === true };
}

function comparisonRow(
	spec: FeatureSpec,
	tierCodes: AccountGroupCode[],
	tierLimits: (TierLimits | undefined)[],
	unserved: FeatureValue,
): ComparisonRow {
	if ("includedIn" in spec) {
		return {
			name: spec.name,
			values: tierCodes.map((code) => ({ type: "boolean", value: spec.includedIn(code) })),
		};
	}

	const format = spec.format ?? quantityCell;
	return {
		name: spec.name,
		values: tierLimits.map((limits) => (limits === undefined ? unserved : format(limits[spec.limit]))),
	};
}

const featureSections: FeatureSection[] = [
	{
		title: "Compute",
		features: [
			{
				name: "Signaloid C0 processor instruction executions per month",
				limit: "DynamicInstructionCount",
				format: numericCell(toStringWithMagnitude),
			},
			{ name: "Concurrent tasks", limit: "ConcurrentTaskCount" },
			{
				name: "Task execution time",
				limit: "ExecutionTimeInMillisecondsCount",
				format: numericCell(toStringWithDurationMagnitude),
			},
			{ name: "Distribution plots per month", limit: "PlotCount" },
		],
	},
	{
		title: "Processor",
		features: [
			{ name: "C0 microarchitectures", limit: "CoreAvailableMicroarchitectures", format: listLengthCell },
			{ name: "Maximum C0Pro processor precision", limit: "CorePrecisionBits" },
			{ name: "Maximum reference execution quality", limit: "CoreReferenceQuality" },
			{
				name: "Maximum attached processor RAM",
				limit: "CoreMemorySizeBytesMaximum",
				format: numericCell(toStringWithByteMagnitude),
			},
			{ name: "Custom C0Pro processor cores", limit: "CoreCount" },
		],
	},
	{
		title: "Builds and repositories",
		features: [
			{ name: "GitHub integration", includedIn: onEveryTier },
			{ name: "GitHub repositories", limit: "RepositoryCount" },
			{
				name: "Build time",
				limit: "BuildTimeInMillisecondsCount",
				format: numericCell(toStringWithDurationMagnitude),
			},
			{ name: "Concurrent builds", limit: "ConcurrentBuildCount" },
			{
				name: "Build registry storage",
				limit: "RegistryStorageBytesMaximum",
				format: numericCell(toStringWithByteMagnitude),
			},
		],
	},
	{
		title: "Data",
		features: [
			{
				name: "Signaloid cloud storage",
				limit: "CloudStorageBytes",
				format: numericCell(toStringWithByteMagnitude),
			},
			{ name: "Signaloid Data Drives", limit: "DataDriveCount" },
			{ name: "Data sources", limit: "DataSourceCount" },
			{ name: "AWS S3 buckets", limit: "BucketCount" },
			{ name: "Data streaming gateways", limit: "GatewayCount" },
		],
	},
	{
		title: "API",
		features: [
			{ name: "API invocations per month", limit: "APIAccessCount" },
			{ name: "API keys", limit: "KeyCount" },
		],
	},
	{
		title: "Enterprise",
		features: [
			{ name: "White labeling", includedIn: onEnterpriseTiers },
			{ name: "Auditable zero data retention", limit: "AuditableZeroDataRetention", format: includedCell },
			{
				name: "Dedicated single-tenant server",
				limit: "DedicatedSingleTenancyPrivateServerOption",
				format: includedCell,
			},
			{
				name: "Additional computation, data storage, and rendered plots",
				includedIn: onEnterpriseTiers,
			},
		],
	},
];

type ComponentData = {
	userCognitoGroups: CognitoAccountGroup[];
	retryingCatalogFetch: boolean;
};

export default defineComponent({
	name: "TierComparisonTable",
	components: { CheckmarkFilled16, CloseOutline16, SkeletonBlock, ErrorEmptyState },
	props: {
		monthlySubscription: { type: Boolean, default: true },
		userSubscription: { type: Object as PropType<SubscriptionConfig> },
	},
	data: (): ComponentData => ({
		userCognitoGroups: [],
		retryingCatalogFetch: false,
	}),
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	emits: {
		"subscribe-clicked": (_code: AccountGroupCode) => true,
		"customer-portal": () => true,
		"free-trial-clicked": (_stopLoading: () => void) => true,
	},
	computed: {
		tierDetails(): AccountTierDetail[] {
			return listedTiers;
		},
		tierCodes(): AccountGroupCode[] {
			return this.tierDetails.map((tier) => tier.code);
		},
		tierLimits(): (TierLimits | undefined)[] {
			return this.tierCodes.map((code) => catalogEntryForTier(this.userStore.tierCatalog, code)?.Limits);
		},
		comparisonSections(): ComparisonSection[] {
			if (this.userStore.tierCatalogLoadFailed) {
				return [];
			}

			const unservedCell = this.userStore.tierCatalog === undefined ? LOADING_CELL : ABSENT_CELL;

			return featureSections.map((section) => ({
				title: section.title,
				features: section.features.map((spec) =>
					comparisonRow(spec, this.tierCodes, this.tierLimits, unservedCell),
				),
			}));
		},
		showsSafetyLimitFootnote(): boolean {
			return this.comparisonSections.some((section) =>
				section.features.some((feature) => feature.values.some((cell) => cell.value === SAFETY_LIMITED_TEXT)),
			);
		},
		userSubscriptionTierCode(): AccountGroupCode | "" {
			return groupCode(this.userStore.currentUserSubscriptions?.Tier ?? "");
		},
		userCanUseCustomerPortal(): boolean {
			return this.userCognitoGroups.includes("CustomerPortalUser");
		},
		userEligibleForTrial(): boolean {
			const noFreeTrial =
				!this.userSubscription?.FreeTrial || this.userSubscription?.FreeTrial.TrialStart === null;
			return noFreeTrial;
		},
	},
	methods: {
		isDowngrade(tierCode: AccountGroupCode): boolean {
			return (
				this.userSubscriptionTierCode !== "" &&
				tierCode !== this.userSubscriptionTierCode &&
				compareTierCodes(this.userSubscriptionTierCode, tierCode)
			);
		},
		getButtonText(tier: AccountTierDetail): string {
			if (tier.code === this.userSubscriptionTierCode) {
				return "Manage Subscription";
			}
			if (this.userEligibleForTrial && tier.code === "pro-tier") {
				return "Start Trial";
			}
			if (this.isDowngrade(tier.code)) {
				return `Downgrade to ${tier.title}`;
			}
			return tier.buttonText;
		},
		getButtonVariant(tier: AccountTierDetail): "outlined" | "flat" {
			if (this.getButtonDisabled(tier) || this.isDowngrade(tier.code)) {
				return "outlined";
			}
			return "flat";
		},
		getButtonColor(tier: AccountTierDetail): string | undefined {
			if (this.getButtonDisabled(tier) || this.isDowngrade(tier.code)) {
				return undefined;
			}
			return "primary";
		},
		getButtonDisabled(tier: AccountTierDetail): boolean {
			const userTierCode = this.userSubscriptionTierCode;
			if (userTierCode === "") {
				return false;
			}
			if (tier.code === userTierCode && this.userCanUseCustomerPortal) {
				return userTierCode === "free-tier";
			}
			if (tier.code === "free-tier" && userTierCode === "free-tier") {
				return true;
			}
			return false;
		},
		getPriceDisplay(tier: AccountTierDetail): string {
			const subtitle = this.monthlySubscription ? tier.monthlyModeSubtitles[0] : tier.yearlyModeSubtitles[0];
			if (subtitle.toLowerCase().includes("contact")) {
				return "Contact us";
			}
			return subtitle;
		},
		async loadTierCatalog() {
			this.retryingCatalogFetch = true;
			await this.userStore.fetchTierCatalog();
			this.retryingCatalogFetch = false;
		},
		handleButtonClick(tier: AccountTierDetail) {
			if (this.userCanUseCustomerPortal && tier.code === this.userSubscriptionTierCode && tier.requiresPayment) {
				this.$emit("customer-portal");
				return;
			}
			if (this.userEligibleForTrial && tier.code === "pro-tier" && tier.requiresPayment) {
				this.$emit("free-trial-clicked", () => {});
				return;
			}
			this.$emit("subscribe-clicked", tier.code);
		},
	},
	async mounted() {
		await this.loadTierCatalog();

		const currentUserGroups = await this.userStore.getCurrentUserGroups();
		if (currentUserGroups) {
			this.userCognitoGroups = currentUserGroups;
		}
	},
});
</script>

<style lang="scss" scoped>
.comparison-table-container {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.comparison-table-wrapper {
	overflow-x: auto;
}

.comparison-table {
	width: 100%;
	border-collapse: collapse;
	font-size: var(--sgnl-font-size-paragraph-s);
	line-height: var(--sgnl-font-line-height-paragraph-s);
	letter-spacing: var(--sgnl-font-letter-spacing0);
}

.comparison-table thead {
	position: sticky;
	top: 0;
	z-index: 2;
}

.feature-column-header,
.tier-column-header {
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	text-align: left;
	color: var(--sgnl-default-primary-text);
	background: var(--sgnl-default-primary-background-mono);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	white-space: nowrap;
}

.feature-column-header {
	font-weight: 600;
	position: sticky;
	left: 0;
	z-index: 3;
	background: var(--sgnl-default-primary-background-mono);
}

.tier-column-header {
	text-align: center;
}

.tier-current-plan-label {
	display: block;
	white-space: nowrap;
}

.feature-name-cell {
	padding: var(--sgnl-space-10) var(--sgnl-space-16);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-right: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	position: sticky;
	left: 0;
	background: var(--sgnl-default-primary-background);
	z-index: 1;
}

.feature-value-cell {
	padding: var(--sgnl-space-10) var(--sgnl-space-16);
	text-align: center;
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-right: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	white-space: nowrap;
}

.feature-value-cell:last-child {
	border-right: none;
}

/* Vertical lines on header cells */
.feature-column-header {
	border-right: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

.tier-column-header {
	border-right: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

.tier-column-header:last-child {
	border-right: none;
}

/* Alternating row backgrounds */
.comparison-table tbody tr:nth-child(even):not(.action-row) .feature-name-cell,
.comparison-table tbody tr:nth-child(even):not(.action-row) .feature-value-cell {
	background: var(--sgnl-brand-background-inverse);
}

.price-row .feature-name-cell {
	font-weight: 600;
}

.section-title-cell {
	padding: var(--sgnl-space-24) var(--sgnl-space-16) var(--sgnl-space-10);
	text-align: left;
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
}

.action-row .feature-name-cell,
.action-row .feature-value-cell {
	border-bottom: none;
}

.action-value-cell {
	padding: var(--sgnl-space-16);
	white-space: normal;
}
</style>
